import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { quoteApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { Alert } from '../ui.tsx';
import { customerPaysCents } from './plain-price.tsx';
import { formatMargin, marginTone } from '../schedule/job-profit-card.tsx';
import { calculateQuote, formatCents, type QuoteCalculationResult } from '@shared/calculations/pricing.ts';
import {
  estimateJobCost,
  marginBasisPoints,
  priceForTargetMargin,
  quoteTaxShare,
  visitsPerMonth,
  withTax,
  withoutTax,
} from '@shared/calculations/job-profit.ts';
import { calculationInputFor } from '../../hooks/use-quote-editor.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { JobProfitPattern, Quote } from '@shared/types/index.ts';

/**
 * The quote screen's profit line (migration 0035): "Estimated profit $X (Y%)",
 * from the quote's own price and estimated hours and the pay of the crew who
 * usually work for this customer — or the team's average when nobody does
 * yet — plus supplies and the expected card fee. Computed here, beside the
 * price preview, so it moves as the quote is edited.
 *
 * Pro, and the owner's and managers' alone: an estimator prices quotes but
 * does not see pay. A nudge, never a block — sending is never refused for it.
 */
export interface QuoteProfitEstimate {
  revenueCents: number;
  costCents: number;
  profitCents: number;
  marginBasisPoints: number | null;
  targetMarginBasisPoints: number | null;
  /** Below the company's target. */
  belowTarget: boolean;
  /** What the customer would pay at the target margin, tax included. */
  suggestedPriceCents: number | null;
  source: 'usual_crew' | 'team_average' | 'none';
  payMissing: boolean;
  monthly: boolean;
  patterns: JobProfitPattern[];
}

export function useQuoteProfit(quote: Quote, preview?: QuoteCalculationResult): QuoteProfitEstimate | null {
  const { can } = useSession();
  const allowed = usePlanFeature('profit_insights') && can('reports:read');
  const inputs = useQuery({
    queryKey: ['quote-profit-inputs', quote.id],
    queryFn: () => quoteApi.profitInputs(quote.id),
    enabled: allowed,
    staleTime: 60_000,
    retry: false,
  });
  if (!allowed || !inputs.data) return null;
  const data = inputs.data;
  const calculated = preview ?? calculateQuote(calculationInputFor(quote));

  // The tax inside what the customer pays, at the quote's own share of it.
  // A manual total keeps the calculated tax, so it is a share of that total.
  const taxShare = quoteTaxShare(calculated.taxCents, quote.totals.totalOverrideCents ?? calculated.totalCents);
  const customerPays = customerPaysCents(quote, calculated.totalCents);
  const revenueCents = withoutTax(customerPays, taxShare);
  const monthly = calculated.billingBasis === 'monthly';
  const estimate = estimateJobCost(
    {
      jobBasisCents: revenueCents,
      laborMinutes: calculated.estimatedMinutes,
      crewSize: Math.max(1, quote.crewSize || data.crew.length || 1),
      // A monthly price and its minutes are a month of visits, each paid for.
      visits: monthly ? visitsPerMonth(quote.visitsPerWeek) : 1,
    },
    {
      crew: data.crew,
      team: data.team,
      owner: data.owner,
      suppliesBasisPoints: data.suppliesBasisPoints,
      cardFee: data.cardFee,
    },
  );
  const profitCents = revenueCents - estimate.totalCostCents;
  const margin = marginBasisPoints(profitCents, revenueCents);
  const target = data.targetMarginBasisPoints;
  const belowTarget = !estimate.payMissing && target !== null && margin !== null && margin < target;
  const preTaxTarget = belowTarget ? priceForTargetMargin(estimate, target) : null;
  // A manual total carries the calculated tax as a fixed amount, whatever
  // the total is set to: the target price is that tax on top, not the
  // target grossed up at the share the current total gives it.
  const overridden = quote.totals.totalOverrideCents != null;
  const suggestedPriceCents =
    preTaxTarget === null
      ? null
      : overridden
        ? preTaxTarget + Math.max(0, calculated.taxCents)
        : withTax(preTaxTarget, taxShare);
  return {
    revenueCents,
    costCents: estimate.totalCostCents,
    profitCents,
    marginBasisPoints: margin,
    targetMarginBasisPoints: target,
    belowTarget,
    suggestedPriceCents,
    source: data.source,
    payMissing: estimate.payMissing,
    monthly,
    patterns: data.patterns,
  };
}

/** The line under the price in the totals panel. */
export function QuoteProfitLine({
  quote,
  preview,
  currencyCode,
}: {
  quote: Quote;
  preview: QuoteCalculationResult;
  currencyCode: string;
}) {
  const { t } = useTranslation();
  const { user } = useSession();
  const estimate = useQuoteProfit(quote, preview);
  if (!estimate) return null;
  const money = (cents: number) => formatCents(cents, currencyCode, getDateLocale());

  if (estimate.payMissing) {
    return (
      <p className="mt-1 text-sm text-[color:var(--color-muted)]">
        {t('quoteProfit.payMissing')}{' '}
        {user?.role === 'owner' ? (
          <Link to="/settings#settings-teamPay" className="underline">
            {t('quoteProfit.setPay')}
          </Link>
        ) : null}
      </p>
    );
  }

  const amount = money(estimate.profitCents);
  return (
    <div className="mt-1 text-sm">
      <p className={`font-semibold ${marginTone({ ...estimate })}`}>
        {estimate.marginBasisPoints === null
          ? t(estimate.monthly ? 'quoteProfit.lineMonthlyNoMargin' : 'quoteProfit.lineNoMargin', { amount })
          : t(estimate.monthly ? 'quoteProfit.lineMonthly' : 'quoteProfit.line', {
              amount,
              margin: formatMargin(estimate.marginBasisPoints),
            })}
      </p>
      <p className="text-[color:var(--color-muted)]">
        {estimate.source === 'usual_crew' ? t('quoteProfit.sourceUsualCrew') : t('quoteProfit.sourceTeam')}
      </p>
    </div>
  );
}

/**
 * The warning before sending: below target, with the price that would reach
 * it. The owner can still send — this only says so.
 */
export function QuoteProfitWarning({ quote, currencyCode }: { quote: Quote; currencyCode: string }) {
  const { t } = useTranslation();
  const estimate = useQuoteProfit(quote);
  if (!estimate || estimate.payMissing) return null;
  const money = (cents: number) => formatCents(cents, currencyCode, getDateLocale());
  const pattern = estimate.patterns[0];
  if (!estimate.belowTarget && !pattern) return null;
  return (
    <div className="mb-4">
      <Alert
        tone="warning"
        title={estimate.belowTarget ? t('quoteProfit.warningTitle') : t('quoteProfit.patternTitle')}
      >
        {estimate.belowTarget && estimate.marginBasisPoints !== null && estimate.targetMarginBasisPoints !== null ? (
          <p>
            {estimate.suggestedPriceCents !== null
              ? t('quoteProfit.belowTarget', {
                  margin: formatMargin(estimate.marginBasisPoints),
                  target: formatMargin(estimate.targetMarginBasisPoints),
                  price: money(estimate.suggestedPriceCents),
                })
              : t('quoteProfit.belowTargetNoPrice', {
                  margin: formatMargin(estimate.marginBasisPoints),
                  target: formatMargin(estimate.targetMarginBasisPoints),
                })}
          </p>
        ) : null}
        {pattern ? (
          <p className={estimate.belowTarget ? 'mt-1' : undefined}>
            {pattern.overrunBasisPoints !== null && pattern.overrunBasisPoints >= 1500
              ? t('quoteProfit.patternOverrun', {
                  percent: Math.round(pattern.overrunBasisPoints / 100),
                  count: pattern.visits,
                })
              : t('quoteProfit.patternMargin', {
                  points: Math.round(Math.abs(pattern.marginGapBasisPoints) / 100),
                  count: pattern.visits,
                })}
          </p>
        ) : null}
        <p className="mt-1 text-sm">{t('quoteProfit.sendAnyway')}</p>
      </Alert>
    </div>
  );
}
